import { api } from "..";
import { CONTROL_TIMEOUT_MS } from "./constants";

/**
 * POST to an upload control-plane route (initialize, sign-parts, list-parts,
 * complete, abort). Goes through the shared axios instance so a token that
 * expires during a long upload is refreshed, but with its own timeout (the
 * shared 10 s is too short for initialize and complete) and the caller's
 * abort signal.
 */

/**
 * Messages for the statuses every upload route shares (backend `upload_actions_mixin`).
 * A 400 is the exception: it names what was wrong with this request (part count, size,
 * extension, part number), so the backend's text is shown as is.
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: "The server rejected this upload request.",
  403: "You do not have permission to continue this upload.",
  404: "The upload session has expired. Upload the file again.",
  409: "This file has already been uploaded.",
  500: "File storage failed. Try again in a few minutes."
};

export class UploadRequestError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = "UploadRequestError";
  }
}

/** The backend no longer holds this upload session for this user, so it cannot be resumed. */
export const isSessionGone = (error: unknown): boolean => {
  const status = (error as { status?: number })?.status;
  return status === 404 || status === 403;
};

export const postUpload = async <T>(path: string, body: unknown, options: { timeout?: number; signal?: AbortSignal } = {}): Promise<T> => {
  try {
    const res = await api.post<T>(path, body, { timeout: options.timeout ?? CONTROL_TIMEOUT_MS, signal: options.signal });
    return res.data;
  } catch (error) {
    // The response interceptor has already turned this into a NetworkError with a status
    // and the backend's message (`handleError` reads `error` as well as `message`).
    const status = (error as { status?: number })?.status;
    const backendMessage = (error as Error)?.message;
    const message =
      status === 400
        ? backendMessage || STATUS_MESSAGES[400]
        : (status && STATUS_MESSAGES[status]) || backendMessage || "Upload request failed.";
    throw new UploadRequestError(message, status);
  }
};
